import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import { cn } from '../cn';
import Button from '../Input2/Button';
import Input from '../Input2/Input';
import Tooltip from '../Input2/Tooltip';
import { Arrows, General, Security } from '../StrokeIcons';

export type ContactFieldReadOnlyValue = {
  data: string;
  source: string;
  primary: boolean;
  verified?: boolean | null;
  /** Why the value is invalid (e.g. 'firm_number'), when known. Drives the invalid-reason badge. */
  invalidReason?: string | null;
  confidenceScore?: number | null;
  createdAt?: Date | string | null;
};

/** Human label for an `invalidReason`, or null when there's nothing to show. */
export function invalidReasonLabel(reason: string | null | undefined): string | null {
  if (reason === 'firm_number') return 'Firm number';
  return null;
}

export type ContactFieldReadOnlyProps = {
  title: string;
  icon?: ReactNode;
  fields?: ContactFieldReadOnlyValue[];
  onRemovePrimary?: (source: string) => void;
  onMakePrimary?: (source: string) => void;
  onDelete?: (source: string) => void;
  /** When provided, shows a verify icon next to each value. */
  onVerify?: (source: string) => void | Promise<void>;
  /** When provided (phone field), shows a "Mark as firm number" action next to each value that
      isn't already flagged as one. Receives the phone number (the value's `data`). */
  onMarkFirmNumber?: (phone: string) => void | Promise<void>;
  /** Adds a new value with source 'input'. When provided, the + button is shown. */
  onAdd?: (value: string) => void | Promise<void>;
  /** Controlled expanded state. If omitted, expansion is managed internally. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** Controlled adding state. If omitted, adding is managed internally. */
  isAdding?: boolean;
  onIsAddingChange?: (next: boolean) => void;
  /** When true, the expanded panel is NOT rendered inline. The parent is expected
      to render it (e.g. as a full-width sibling below a grid). */
  hideInlineExpandedPanel?: boolean;
  /** Formats each value purely for display (e.g. phone numbers). The underlying
      data is untouched — only the rendered string changes. Defaults to identity. */
  formatValue?: (value: string) => string;
};

function sourceLabel(source: string): string {
  if (source.startsWith('enhance') || source.startsWith('enrich')) return 'Enrichment';
  if (source.startsWith('basic-enhance') || source.startsWith('basic-enrich')) return 'Basic enrichment';
  if (source === 'linkedin-api') return 'LinkedIn (API)';
  if (source === 'linkedin-electron') return 'LinkedIn (Electron)';
  if (source.startsWith('linkedin')) return 'LinkedIn';
  if (source.startsWith('crm')) return 'CRM';
  if (source.startsWith('input')) return 'Manual input';
  if (source.startsWith('csv')) return 'CSV upload';
  if (source.startsWith('ai')) return 'AI';
  if (source.startsWith('close')) return 'Close';
  return source;
}

function formatUpdated(value: Date | string | null | undefined): string | null {
  if (value == null) return null;
  const dt = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value);
  if (!dt.isValid) return null;
  return dt.toRelative();
}

export function ContactFieldAddRow({
  onSave,
  onCancel,
}: {
  onSave: (value: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed);
      setValue('');
      onCancel();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-stretch py-0.5">
      <Input
        size="sm"
        autoFocus
        placeholder="New value..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSave();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        className="flex-1 rounded-r-none border-r-0"
        disabled={saving}
      />
      <Button
        size="sm"
        color="green"
        icon={<General.CheckCircle />}
        tooltip="Save"
        onClick={() => void handleSave()}
        disabled={saving || value.trim().length === 0}
        className="rounded-l-none"
      />
    </div>
  );
}

export function ContactFieldExpandedRows({
  others,
  onMakePrimary,
  onDelete,
  onVerify,
  onMarkFirmNumber,
  addRow,
  formatValue,
}: {
  others: ContactFieldReadOnlyValue[];
  onMakePrimary?: (source: string) => void;
  onDelete?: (source: string) => void;
  onVerify?: (source: string) => void | Promise<void>;
  onMarkFirmNumber?: (phone: string) => void | Promise<void>;
  addRow?: ReactNode;
  formatValue?: (value: string) => string;
}) {
  const fmt = formatValue ?? ((v: string) => v);
  return (
    <div className="flex flex-col">
      {addRow}
      {others.map((f, i) => (
        <div
          key={String(i) + f.source + f.data}
          className="group/row flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-200">
          <p
            className={cn(
              'min-w-0 truncate',
              f.verified === true && 'text-green-600 dark:text-green-400',
              f.verified === false && 'text-red-600 dark:text-red-400',
            )}>
            {fmt(f.data)}
          </p>
          {f.verified === false && invalidReasonLabel(f.invalidReason) && (
            <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] font-medium uppercase tracking-wide text-red-600 dark:bg-red-950 dark:text-red-400">
              {invalidReasonLabel(f.invalidReason)}
            </span>
          )}
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-haiti-500 dark:text-haiti-400">
            {sourceLabel(f.source)}
          </span>
          <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100">
            {onMakePrimary && (
              <Button
                size="xs"
                color="green"
                text
                noPadding
                icon={<General.CheckCircle />}
                tooltip="Make primary"
                onClick={() => onMakePrimary(f.source)}
              />
            )}
            {onVerify && (
              <Button
                size="xs"
                color="blue"
                text
                noPadding
                icon={<Security.Shield01 />}
                tooltip={
                  f.verified === true ? 'Re-verify (valid)' : f.verified === false ? 'Re-verify (invalid)' : 'Verify'
                }
                onClick={() => void onVerify(f.source)}
              />
            )}
            {onMarkFirmNumber && f.invalidReason !== 'firm_number' && (
              <Button
                size="xs"
                color="yellow"
                text
                noPadding
                icon={<General.Building02 />}
                tooltip="Mark as firm number"
                onClick={() => void onMarkFirmNumber(f.data)}
              />
            )}
            {onDelete && (
              <Button
                size="xs"
                color="red"
                text
                noPadding
                icon={<General.Trash01 />}
                tooltip="Delete"
                onClick={() => onDelete(f.source)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ContactFieldReadOnly({
  title,
  icon,
  fields,
  onRemovePrimary,
  onMakePrimary,
  onDelete,
  onVerify,
  onMarkFirmNumber,
  onAdd,
  expanded: expandedProp,
  onExpandedChange,
  isAdding: isAddingProp,
  onIsAddingChange,
  hideInlineExpandedPanel,
  formatValue,
}: ContactFieldReadOnlyProps) {
  const fmt = formatValue ?? ((v: string) => v);
  const primary = fields?.find((f) => f.primary);
  const others = fields?.filter((f) => f !== primary) ?? [];
  const otherCount = others.length;
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const setExpanded = (next: boolean) => {
    if (onExpandedChange) onExpandedChange(next);
    if (expandedProp === undefined) setInternalExpanded(next);
  };
  const [internalIsAdding, setInternalIsAdding] = useState(false);
  const isAdding = isAddingProp ?? internalIsAdding;
  const setIsAdding = (next: boolean) => {
    if (onIsAddingChange) onIsAddingChange(next);
    if (isAddingProp === undefined) setInternalIsAdding(next);
  };

  // Begin (or cancel) adding a value. Shared by the "+" button and the
  // empty-state placeholder so clicking either does the same thing.
  const handleToggleAdd = () => {
    if (isAdding) {
      setIsAdding(false);
    } else {
      setIsAdding(true);
      setExpanded(true);
    }
  };

  // If the last "other value" gets deleted while we're expanded (and not in
  // the middle of adding), auto-collapse the field.
  useEffect(() => {
    if (expanded && otherCount === 0 && !isAdding) {
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, otherCount, isAdding]);

  const tooltipContent =
    primary || otherCount > 0 ? (
      <div className="flex flex-col gap-2 normal-case tracking-normal">
        {primary && (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">
              {fmt(primary.data)}
              {primary.verified === true && (
                <span className="ml-1 font-semibold text-green-400">· Valid</span>
              )}
              {primary.verified === false && (
                <span className="ml-1 font-semibold text-red-400">· Invalid</span>
              )}
            </span>
            <span className="opacity-70">
              From {sourceLabel(primary.source)}
              {formatUpdated(primary.createdAt) && ` · ${formatUpdated(primary.createdAt)}`}
            </span>
          </div>
        )}
        {otherCount > 0 && (
          <div className="flex flex-col gap-0.5">
            <span className="opacity-70">
              {otherCount} other possible value{otherCount === 1 ? '' : 's'}:
            </span>
            {others.map((f, i) => (
              <span key={String(i) + f.source + f.data}>
                {fmt(f.data)} <span className="opacity-60">— {sourceLabel(f.source)}</span>
                {f.verified === true && <span className="ml-1 text-green-400">· Valid</span>}
                {f.verified === false && <span className="ml-1 text-red-400">· Invalid</span>}
              </span>
            ))}
          </div>
        )}
      </div>
    ) : undefined;

  return (
    <Tooltip
      tooltip={expanded ? undefined : tooltipContent}
      tooltipPosition="left"
      tooltipFallbacks="top, bottom, flip-inline"
      className={cn(
        'block',
        expanded && [
          'relative isolate',
          "before:absolute before:inset-x-2 before:inset-y-0 before:-z-10 before:content-['']",
          'before:bg-slate-100 dark:before:bg-slate-800/40',
          hideInlineExpandedPanel ? 'before:rounded-t-xl' : 'before:rounded-xl',
        ],
      )}>
      <div
        className={cn(
          'group/field flex flex-col px-4 pt-2.5',
          expanded && !hideInlineExpandedPanel ? '' : 'pb-1',
        )}>
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-haiti-500 dark:text-haiti-400">
            {icon && (
              <div className="size-3 shrink-0 text-gray-400 dark:text-gray-500 [&>svg]:size-full">{icon}</div>
            )}
            <span>{title}</span>
            {otherCount > 0 && <span>({otherCount})</span>}
            <div className="flex items-center gap-0.5">
              {otherCount > 0 && (
                <Button
                  size="xs"
                  text
                  noPadding
                  icon={
                    <Arrows.ChevronDown
                      className={cn('transition-transform', expanded && 'rotate-180')}
                    />
                  }
                  tooltip={expanded ? 'Hide other values' : 'Show other values'}
                  onClick={() => setExpanded(!expanded)}
                  className={cn(
                    'transition-opacity',
                    !expanded && 'opacity-0 group-hover/field:opacity-100',
                  )}
                />
              )}
              {onAdd && (
                <Button
                  size="xs"
                  color={isAdding ? 'red' : 'gray'}
                  text
                  noPadding
                  icon={isAdding ? <General.XCircle /> : <General.Plus />}
                  tooltip={isAdding ? 'Cancel' : 'Add'}
                  onClick={handleToggleAdd}
                  className={cn(
                    'transition-opacity',
                    !isAdding && 'opacity-0 group-hover/field:opacity-100',
                  )}
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 text-base font-medium text-neutral-600 dark:text-neutral-100">
            {primary && (
              <>
                <p
                  className={cn(
                    'min-w-0 truncate',
                    primary.verified === true && 'text-green-600 dark:text-green-400',
                    primary.verified === false && 'text-red-600 dark:text-red-400',
                  )}>
                  {fmt(primary.data)}
                </p>
                {primary.verified === false && invalidReasonLabel(primary.invalidReason) && (
                  <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] font-medium uppercase tracking-wide text-red-600 dark:bg-red-950 dark:text-red-400">
                    {invalidReasonLabel(primary.invalidReason)}
                  </span>
                )}
                {onMarkFirmNumber && primary.invalidReason !== 'firm_number' && (
                  <Button
                    size="xs"
                    color="yellow"
                    text
                    noPadding
                    icon={<General.Building02 />}
                    tooltip="Mark as firm number"
                    onClick={() => void onMarkFirmNumber(primary.data)}
                    className="opacity-0 transition-opacity group-hover/field:opacity-100"
                  />
                )}
                {onVerify && (
                  <Button
                    size="xs"
                    color="blue"
                    text
                    noPadding
                    icon={<Security.Shield01 />}
                    tooltip={
                      primary.verified === true
                        ? 'Re-verify (valid)'
                        : primary.verified === false
                          ? 'Re-verify (invalid)'
                          : 'Verify'
                    }
                    onClick={() => void onVerify(primary.source)}
                    className="opacity-0 transition-opacity group-hover/field:opacity-100"
                  />
                )}
                {onRemovePrimary && (
                  <Button
                    size="xs"
                    color="red"
                    text
                    noPadding
                    icon={<General.XCircle />}
                    tooltip="Remove default"
                    onClick={() => onRemovePrimary(primary.source)}
                    className="opacity-0 transition-opacity group-hover/field:opacity-100"
                  />
                )}
              </>
            )}
            {!primary && onAdd && (
              <Button
                text
                size="lg"
                tooltip={isAdding ? 'Cancel' : 'Add'}
                onClick={handleToggleAdd}
                className="h-auto w-auto min-w-0 cursor-pointer truncate px-0 font-medium text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
                unknown
              </Button>
            )}
            {!primary && !onAdd && (
              <p className="min-w-0 truncate text-slate-400 dark:text-slate-500">unknown</p>
            )}
          </div>
          {expanded && (otherCount > 0 || isAdding) && !hideInlineExpandedPanel && (
            <ContactFieldExpandedRows
              others={others}
              onMakePrimary={onMakePrimary}
              onDelete={onDelete}
              onVerify={onVerify}
              onMarkFirmNumber={onMarkFirmNumber}
              formatValue={formatValue}
              addRow={
                isAdding && onAdd ? (
                  <ContactFieldAddRow
                    onSave={onAdd}
                    onCancel={() => setIsAdding(false)}
                  />
                ) : undefined
              }
            />
          )}
        </div>
      </div>
    </Tooltip>
  );
}
